import { Box, Theme, Tooltip } from '@mui/material';
import { ReadonlyVec3 } from 'gl-matrix';
import { useId, useRef } from 'react';
import { Modality } from '~/domain/dicom/modality';
import { IntensityHistogram } from '~/domain/patient/image3d';
import { WindowLevel } from '~/domain/patient/windowLevel';
import {
    FusionColorMapping,
    grayscaleFusionColorMapping,
} from '~/domain/viewport/fusionBlend';
import { OverlayLabel } from '~/components/OverlayLabel';
import { useElementSize } from '~/hooks/useElementSize';
import { rgbToString } from '~/utils/colorUtils';

/** The narrowest the strip draws at, in CSS px: below it the labels no longer fit. */
export const MIN_STRIP_WIDTH_PX = 120;
const MAX_STRIP_WIDTH_PX = 300;
/** Height of the bar itself, in CSS px. */
export const STRIP_HEIGHT_PX = 15;
const LABELS_HEIGHT_PX = 8;
const ROW_GAP_PX = 2;
/** Distance of the bar from the top of the strip, in CSS px. */
export const STRIP_BAR_TOP_PX = LABELS_HEIGHT_PX + ROW_GAP_PX;
/** Height of the whole strip, its label rows included, in CSS px. */
export const STRIP_BLOCK_HEIGHT_PX =
    LABELS_HEIGHT_PX +
    ROW_GAP_PX +
    STRIP_HEIGHT_PX +
    ROW_GAP_PX +
    LABELS_HEIGHT_PX;
// Dark halo behind the overlay markers (edge carets and the reference line)
// so they keep contrast over the white end of the window gradient and the
// pale histogram bars.
const MARKER_HALO_COLOR = 'rgba(0, 0, 0, 0.85)';
// Window-edge markers: a white caret (filled and dark-outlined so it reads
// on dark, the bright gradient end, and pale bars alike) hung from the top
// rail over a thin full-height guide line that pins the exact column.
const EDGE_MARKER_COLOR = '#ffffff';
const EDGE_GUIDE_COLOR = 'rgba(255, 255, 255, 0.6)';
const EDGE_CARET_HALF_PX = 5;
const EDGE_CARET_HEIGHT_PX = 5;
// Thin dark edge on each histogram bar so the distribution stays legible
// over the bright end of the gradient, where pale bars would otherwise wash
// out against the light background.
const BAR_OUTLINE_COLOR = 'rgba(0, 0, 0, 0.55)';
// Scrim over the out-of-window regions so the window band reads as the lit
// area. Kept moderate so the outlined bars stay visible through it.
const WINDOW_SCRIM_COLOR = 'rgba(8, 10, 14, 0.45)';

// When the current window extends past the percentile-clipped histogram,
// widen the displayed value-axis by this fraction of the histogram range on
// the extended side(s). Without it, presets that fall outside the clipped
// range (e.g. Metal with WC=2800 on data clipped to ~1500 HU) would pin the
// window band flush to the strip edge and hide where the window actually sits.
const DISPLAY_PAD_FRACTION = 0.05;

const LABEL_FONT_SIZE_PX = 9;
// Monospace with tabular figures so the value labels are fixed-width — no
// reflow as WW/WC change — and consistent with the rest of the overlay.
const LABEL_FONT_FAMILY = "'Roboto Mono', monospace";
// Breathing room (px) required between an edge value and its tick to keep the
// label anchored; below this the two values collapse into one range token.
const LABEL_PAD_PX = 4;
// Solid dark pill behind each 9px overlay label so it stays legible over
// bright or busy parts of the underlying image — the labels sit on
// transparent rows above and below the strip, not on the opaque strip.
const LABEL_PILL_BG = 'rgba(0, 0, 0, 0.65)';
// Horizontal padding (px) inside the pill; counted into the fit test so the
// pill, not just its text, is what must fit beside the tick.
const LABEL_PILL_PAD_X = 4;
const LABEL_PILL_SX = {
    display: 'inline-block',
    bgcolor: LABEL_PILL_BG,
    color: 'rgba(255, 255, 255, 0.95)',
    px: `${LABEL_PILL_PAD_X}px`,
    borderRadius: '3px',
    fontFamily: LABEL_FONT_FAMILY,
    fontVariantNumeric: 'tabular-nums',
};

// Width of a string at the label font, used to decide whether both edge
// values fit anchored. A lazily-created offscreen canvas measures the real
// font; environments without a 2D context (e.g. jsdom) fall back to a rough
// per-character estimate — harmless because the collapse decision is also
// gated on a measured container width, which those environments report as 0.
let measureCanvas: HTMLCanvasElement | null = null;
function measureTextPx(text: string, font: string): number {
    if (typeof document !== 'undefined') {
        measureCanvas ??= document.createElement('canvas');
        const ctx = measureCanvas.getContext('2d');
        if (ctx) {
            ctx.font = font;
            return ctx.measureText(text).width;
        }
    }
    return text.length * (LABEL_FONT_SIZE_PX * 0.6);
}

/**
 * Whether both window-edge values fit anchored to their ticks — i.e. each
 * value's pixel width fits the strip region outside its tick (lower in
 * [0, leftTick], upper in [rightTick, 100]) with `padPx` to spare. Returns
 * true when the strip width is unmeasured (<= 0) so labels default to
 * anchored (e.g. before layout, or in tests). When false the caller
 * collapses the two values into a single centred range token.
 */
export function shouldAnchorEdgeLabels(
    stripWidthPx: number,
    markerLeftPct: number,
    markerRightPct: number,
    lowerLabelPx: number,
    upperLabelPx: number
): boolean {
    if (stripWidthPx <= 0) {
        return true;
    }
    const lowerRegionPx = (markerLeftPct / 100) * stripWidthPx;
    const upperRegionPx = ((100 - markerRightPct) / 100) * stripWidthPx;
    return (
        lowerLabelPx <= lowerRegionPx - LABEL_PAD_PX &&
        upperLabelPx <= upperRegionPx - LABEL_PAD_PX
    );
}

type EdgeAnchor = 'lower' | 'upper';

interface DisplayRange {
    min: number;
    max: number;
}

interface MarkerRange {
    leftPct: number;
    widthPct: number;
}

/**
 * Value-axis range for the strip: the union of the histogram's
 * percentile-clipped range and the current window, padded on extended sides
 * so an out-of-range window band stays visible instead of pinning flush to
 * the edge. Pad is sized off the histogram range (a constant), so the axis
 * stays stable as the window changes. Returns histMin/histMax unchanged on a
 * degenerate histogram so the caller's degenerate handling still applies.
 */
export function computeDisplayRange(
    histMin: number,
    histMax: number,
    wl: WindowLevel
): DisplayRange {
    if (histMax <= histMin) {
        return { min: histMin, max: histMax };
    }
    const pad = (histMax - histMin) * DISPLAY_PAD_FRACTION;
    return {
        min: wl.lowerEdge < histMin ? wl.lowerEdge - pad : histMin,
        max: wl.upperEdge > histMax ? wl.upperEdge + pad : histMax,
    };
}

/**
 * Computes where the [level - WW/2, level + WW/2] window lies inside the
 * given intensity range, expressed as percentages of the bar's value-axis.
 * Returns null when the range is degenerate.
 *
 * Edge cases:
 * - Window entirely outside [min, max] → 0-width marker pinned to the edge.
 * - Window straddles the range → clamped to [0, 100].
 */
export function computeMarkerRange(
    wl: WindowLevel,
    minValue: number,
    maxValue: number
): MarkerRange | null {
    if (maxValue <= minValue) {
        return null;
    }
    const span = maxValue - minValue;
    const lo = Math.max(minValue, wl.lowerEdge);
    const hi = Math.min(maxValue, wl.upperEdge);
    if (hi <= lo) {
        return {
            leftPct: wl.level < minValue ? 0 : 100,
            widthPct: 0,
        };
    }
    return {
        leftPct: ((lo - minValue) / span) * 100,
        widthPct: ((hi - lo) / span) * 100,
    };
}

interface ReferenceMarker {
    /** Strip-coordinate position [0..100]. */
    leftPct: number;
    /** Tooltip explaining the marker (e.g., "Water (0 HU)"). */
    label: string;
}

interface HorizontalStripProps {
    histogram: IntensityHistogram;
    /** Where the histogram's value range starts inside the strip [0..100]. */
    histogramLeftPct: number;
    /** Where the histogram's value range ends inside the strip [0..100]. */
    histogramRightPct: number;
    /** Window lower edge as a strip-coordinate percentage [0..100]. */
    markerLeftPct: number;
    /** Window upper edge as a strip-coordinate percentage [0..100]. */
    markerRightPct: number;
    /** Reference tissue lines (e.g., water at 0 HU for CT). */
    referenceMarkers: ReferenceMarker[];
    barColor: string;
    gradientEndColor: string;
}

const HorizontalStrip = ({
    histogram,
    histogramLeftPct,
    histogramRightPct,
    markerLeftPct,
    markerRightPct,
    referenceMarkers,
    barColor,
    gradientEndColor,
}: HorizontalStripProps) => {
    const gradientId = useId();

    let maxBin = 0;
    for (const b of histogram.bins) {
        if (b > maxBin) {
            maxBin = b;
        }
    }
    // Log-scale the bars so the air spike (typical CT histogram) doesn't
    // crush soft-tissue counts to invisibility.
    const logMaxBin = Math.log(1 + maxBin);
    const numBins = histogram.bins.length;
    const histogramSpanPct = Math.max(0, histogramRightPct - histogramLeftPct);
    const binWidthPct = histogramSpanPct / numBins;

    return (
        <svg
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
            style={{ display: 'block', pointerEvents: 'none' }}
        >
            <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#000" />
                    <stop offset={`${markerLeftPct}%`} stopColor="#000" />
                    <stop
                        offset={`${markerRightPct}%`}
                        stopColor={gradientEndColor}
                    />
                    <stop offset="100%" stopColor={gradientEndColor} />
                </linearGradient>
            </defs>
            <rect
                x={0}
                y={0}
                width={100}
                height={100}
                fill={`url(#${gradientId})`}
            />
            {logMaxBin > 0 &&
                binWidthPct > 0 &&
                histogram.bins.map((count, i) => {
                    if (count <= 0) {
                        return null;
                    }
                    const heightPct = (Math.log(1 + count) / logMaxBin) * 100;
                    const xPct = histogramLeftPct + i * binWidthPct;
                    return (
                        <rect
                            key={i}
                            x={xPct}
                            y={100 - heightPct}
                            width={binWidthPct}
                            height={heightPct}
                            fill={barColor}
                            stroke={BAR_OUTLINE_COLOR}
                            strokeWidth={0.4}
                            vectorEffect="non-scaling-stroke"
                            opacity={0.9}
                        />
                    );
                })}
            {referenceMarkers.map((m) => (
                <g key={m.label}>
                    {/* Dark backing widens each dash into a halo so the
                        reference line reads over bright histogram bars. */}
                    <line
                        x1={m.leftPct}
                        y1={0}
                        x2={m.leftPct}
                        y2={100}
                        stroke={MARKER_HALO_COLOR}
                        strokeWidth={3}
                        strokeDasharray="3 2"
                        vectorEffect="non-scaling-stroke"
                    />
                    <line
                        x1={m.leftPct}
                        y1={0}
                        x2={m.leftPct}
                        y2={100}
                        stroke="rgba(255, 120, 215, 1)"
                        strokeWidth={1.5}
                        strokeDasharray="3 2"
                        vectorEffect="non-scaling-stroke"
                    />
                    <title>{m.label}</title>
                </g>
            ))}
        </svg>
    );
};

function vec3ToRgbCss(values: ReadonlyVec3): string {
    return rgbToString(values[0], values[1], values[2]);
}

// Marks one window edge: a caret hung from the top rail over a thin
// full-height guide line. The caret pops against any part of the strip
// (dark, the bright gradient end, or pale bars), so the bound stays findable
// where a thin line alone washes out; the guide pins the exact column. CSS
// clamp keeps both inside the strip near an edge. pointerEvents stays off —
// this marks the edge, it does not drag it.
const EdgeMarker = ({ leftPct }: { leftPct: number }) => (
    <>
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: `clamp(0px, calc(${leftPct}% - 0.5px), calc(100% - 1px))`,
                width: '1px',
                height: '100%',
                bgcolor: EDGE_GUIDE_COLOR,
                pointerEvents: 'none',
            }}
        />
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: `clamp(${EDGE_CARET_HALF_PX}px, ${leftPct}%, calc(100% - ${EDGE_CARET_HALF_PX}px))`,
                transform: 'translateX(-50%)',
                lineHeight: 0,
                pointerEvents: 'none',
            }}
        >
            <svg
                width={EDGE_CARET_HALF_PX * 2}
                height={EDGE_CARET_HEIGHT_PX}
                viewBox={`0 0 ${EDGE_CARET_HALF_PX * 2} ${EDGE_CARET_HEIGHT_PX}`}
                style={{ display: 'block' }}
            >
                <polygon
                    points={`0.5,0.5 ${EDGE_CARET_HALF_PX * 2 - 0.5},0.5 ${EDGE_CARET_HALF_PX},${EDGE_CARET_HEIGHT_PX - 0.5}`}
                    fill={EDGE_MARKER_COLOR}
                    stroke={MARKER_HALO_COLOR}
                    strokeWidth={1}
                    strokeLinejoin="round"
                />
            </svg>
        </Box>
    </>
);

interface EdgeValueLabelProps {
    leftPct: number;
    value: string;
    /**
     * Which window edge this label annotates. The label is confined to the
     * strip region on the outer side of its tick — the lower-edge value to the
     * left of its tick (right-aligned, ending at the tick), the upper-edge
     * value to the right of its tick (left-aligned, starting at the tick) — so
     * the window band is bracketed by its bounds. {@link WindowEdgeLabels}
     * renders this anchored pair only when both values fit; when they would
     * crowd or spill it collapses them into one token instead, so this layout
     * never has to handle the degenerate cases itself.
     */
    anchor: EdgeAnchor;
}

const EdgeValueLabel = ({ leftPct, value, anchor }: EdgeValueLabelProps) => {
    const clampedLeftPct = Math.max(0, Math.min(100, leftPct));
    // Confine each label to the strip region outside its tick: the lower label
    // fills [0%, tick] right-aligned; the upper label fills [tick, 100%]
    // left-aligned. A 2px inset keeps the two from touching when the band
    // collapses to near-zero width.
    const sideSx =
        anchor === 'lower'
            ? {
                  left: 0,
                  right: `${100 - clampedLeftPct}%`,
                  textAlign: 'right' as const,
                  pr: '2px',
              }
            : {
                  left: `${clampedLeftPct}%`,
                  right: 0,
                  textAlign: 'left' as const,
                  pl: '2px',
              };
    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                ...sideSx,
                fontSize: LABEL_FONT_SIZE_PX,
                lineHeight: `${LABEL_FONT_SIZE_PX}px`,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
            }}
        >
            <Box component="span" sx={LABEL_PILL_SX}>
                {value}
            </Box>
        </Box>
    );
};

interface WindowEdgeLabelsProps {
    /** Lower window edge as a strip-coordinate percentage [0..100]. */
    markerLeftPct: number;
    /** Upper window edge as a strip-coordinate percentage [0..100]. */
    markerRightPct: number;
    lowerLabel: string;
    upperLabel: string;
}

/**
 * The window's lower/upper bound values shown above the strip. When both
 * values fit the strip region outside their tick (lower in [0, leftTick],
 * upper in [rightTick, 100]) they stay anchored to each edge, so each number
 * reads unambiguously as "this bound". When the window is wide (regions too
 * small) or near a strip edge — where anchored labels would crowd or spill
 * off-strip — they collapse into a single ordered "low – high" token centred
 * on the strip. Either way the exact window stays in the strip's hover
 * tooltip. The strip width is observed so the layout re-evaluates as the
 * surrounding panel resizes.
 */
const WindowEdgeLabels = ({
    markerLeftPct,
    markerRightPct,
    lowerLabel,
    upperLabel,
}: WindowEdgeLabelsProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widthPx = useElementSize(containerRef)?.widthPx ?? 0;

    // Anchor only when each value fits its strip region with a small pad.
    // Before the width is measured (0, e.g. in tests) default to anchored so
    // the two bounds render as discrete values.
    const font = `${LABEL_FONT_SIZE_PX}px ${LABEL_FONT_FAMILY}`;
    const anchored = shouldAnchorEdgeLabels(
        widthPx,
        markerLeftPct,
        markerRightPct,
        measureTextPx(lowerLabel, font) + LABEL_PILL_PAD_X * 2,
        measureTextPx(upperLabel, font) + LABEL_PILL_PAD_X * 2
    );

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width: '100%',
                height: LABELS_HEIGHT_PX,
                flexShrink: 0,
            }}
        >
            {anchored ? (
                <>
                    <EdgeValueLabel
                        leftPct={markerLeftPct}
                        value={lowerLabel}
                        anchor="lower"
                    />
                    <EdgeValueLabel
                        leftPct={markerRightPct}
                        value={upperLabel}
                        anchor="upper"
                    />
                </>
            ) : (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        fontSize: LABEL_FONT_SIZE_PX,
                        lineHeight: `${LABEL_FONT_SIZE_PX}px`,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                    }}
                >
                    <Box component="span" sx={LABEL_PILL_SX}>
                        {`${lowerLabel} – ${upperLabel}`}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

function formatPercent(numerator: number, denominator: number): string {
    if (denominator <= 0) {
        return '0%';
    }
    const pct = (numerator / denominator) * 100;
    return pct < 0.01 ? '<0.01%' : `${pct.toFixed(2)}%`;
}

interface HistogramTooltipContentProps {
    windowLevel: WindowLevel;
    histogram: IntensityHistogram;
    formatValue: (stored: number) => string;
}

/**
 * Distribution summary shown on hover: sample mean/median, plus a note when
 * voxels fall beyond the percentile-clipped scale (so the user knows the
 * displayed range hides part of the data, e.g. metal artifacts or contrast).
 */
const HistogramTooltipContent = ({
    windowLevel,
    histogram,
    formatValue: fmt,
}: HistogramTooltipContentProps) => {
    const lines: string[] = [
        `WW ${fmt(windowLevel.window)} · WC ${fmt(windowLevel.level)}`,
        `Sample mean ${fmt(histogram.sampleMean)}, median ${fmt(histogram.sampleMedian)}`,
    ];
    if (histogram.voxelsAboveDisplay > 0) {
        const pct = formatPercent(
            histogram.voxelsAboveDisplay,
            histogram.totalSampled
        );
        lines.push(`↑ ${pct} above scale (data max ${fmt(histogram.dataMax)})`);
    }
    if (histogram.voxelsBelowDisplay > 0) {
        const pct = formatPercent(
            histogram.voxelsBelowDisplay,
            histogram.totalSampled
        );
        lines.push(`↓ ${pct} below scale (data min ${fmt(histogram.dataMin)})`);
    }
    return (
        <Box>
            {lines.map((line) => (
                <Box key={line} sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    {line}
                </Box>
            ))}
        </Box>
    );
};

/** The colours one strip is drawn in. */
export interface HistogramStripColors {
    /** Fill of the histogram bars. */
    readonly bar: string;
    /** The bright end of the window gradient: the colour a fully lit voxel is drawn in. */
    readonly gradientEnd: string;
}

/**
 * The colours a strip takes for the image it explains, under the colour
 * pair the image is fused through. Under the grayscale pair the gradient
 * ends in white and the bars take the theme's primary or secondary colour,
 * so each image's strip is identifiable. Under a tinted pair the gradient
 * ends in the tint the renderer paints that image with, and the bars go
 * neutral so they do not compete with it.
 */
export function histogramStripColors(
    theme: Theme,
    mapping: FusionColorMapping,
    accent: 'primary' | 'secondary'
): HistogramStripColors {
    if (mapping.id === grayscaleFusionColorMapping.id) {
        return {
            bar: theme.palette[accent].main,
            gradientEnd: '#ffffff',
        };
    }
    return {
        bar: 'rgba(220, 220, 220, 0.85)',
        gradientEnd: vec3ToRgbCss(
            accent === 'primary'
                ? mapping.primaryValues
                : mapping.secondaryValues
        ),
    };
}

export interface IntensityHistogramStripProps {
    /** The distribution to draw; nothing is drawn while it is null. */
    histogram: IntensityHistogram | null;
    /** The window the strip explains, in stored intensity. */
    windowLevel: WindowLevel;
    /**
     * The modality the intensities belong to, for the reference marks a
     * modality carries (water at 0 HU on CT). None are drawn when omitted.
     */
    modality?: Modality;
    /** Formats a stored intensity as the surrounding readout reads it, unit included. */
    formatValue: (stored: number) => string;
    colors: HistogramStripColors;
    /** Suffix for the data-testid (e.g. "primary" or "secondary"). */
    testIdSuffix?: string;
    /**
     * Names the image the strip explains, ahead of its accessible name, where
     * the strip stands apart from anything else that names it.
     */
    subject?: string;
}

/**
 * Read-only horizontal histogram of one image's intensities: the
 * distribution with a grayscale band marking the current window and
 * labelled edge ticks at its bounds. Hover reveals distribution stats.
 * Nothing here changes the window; it is a situational-awareness hint, not
 * an input, and every value it shows goes through the caller's formatter so
 * it reads in the unit the readout beside it uses.
 */
const IntensityHistogramStrip = ({
    histogram,
    windowLevel,
    modality,
    formatValue,
    colors,
    testIdSuffix,
    subject,
}: IntensityHistogramStripProps) => {
    if (!histogram) {
        return null;
    }

    const display = computeDisplayRange(
        histogram.minValue,
        histogram.maxValue,
        windowLevel
    );
    const displayRange = display.max - display.min;
    const valueToPct = (v: number): number =>
        displayRange > 0 ? ((v - display.min) / displayRange) * 100 : 0;

    const marker = computeMarkerRange(windowLevel, display.min, display.max);
    const markerLeftPct = marker?.leftPct ?? null;
    const markerRightPct =
        marker !== null
            ? Math.min(100, marker.leftPct + marker.widthPct)
            : null;
    const histogramLeftPct = valueToPct(histogram.minValue);
    const histogramRightPct = valueToPct(histogram.maxValue);

    const testId = testIdSuffix
        ? `window-level-hint-${testIdSuffix}`
        : 'window-level-hint';

    const lowerEdgeLabel = formatValue(windowLevel.lowerEdge);
    const upperEdgeLabel = formatValue(windowLevel.upperEdge);

    // Reference tissue marker: water/calibration zero at 0 HU on CT. Surfaced
    // only when 0 falls inside the displayed range so it stays meaningful and
    // doesn't pile up at an edge.
    const referenceMarkers: ReferenceMarker[] = [];
    if (modality === Modality.CT && display.min < 0 && display.max > 0) {
        referenceMarkers.push({
            leftPct: valueToPct(0),
            label: 'Water (0 HU)',
        });
    }

    const hasMarker = markerLeftPct !== null && markerRightPct !== null;

    const displayMinLabel = formatValue(display.min);
    const displayMaxLabel = formatValue(display.max);
    const hint = hasMarker
        ? `Window level hint: window ${lowerEdgeLabel} to ${upperEdgeLabel}; histogram displayed from ${displayMinLabel} to ${displayMaxLabel}.`
        : `Window level hint: histogram displayed from ${displayMinLabel} to ${displayMaxLabel}.`;
    const ariaLabel = subject ? `${subject}: ${hint}` : hint;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                flex: '1 1 0',
                minWidth: MIN_STRIP_WIDTH_PX,
                maxWidth: MAX_STRIP_WIDTH_PX,
                gap: `${ROW_GAP_PX}px`,
            }}
            data-testid={testId}
            role="img"
            aria-label={ariaLabel}
        >
            {hasMarker && (
                <WindowEdgeLabels
                    markerLeftPct={markerLeftPct}
                    markerRightPct={markerRightPct}
                    lowerLabel={lowerEdgeLabel}
                    upperLabel={upperEdgeLabel}
                />
            )}
            <Tooltip
                placement="top"
                arrow
                title={
                    <HistogramTooltipContent
                        windowLevel={windowLevel}
                        histogram={histogram}
                        formatValue={formatValue}
                    />
                }
            >
                <Box
                    data-testid={`${testId}-strip`}
                    sx={{
                        width: '100%',
                        height: STRIP_HEIGHT_PX,
                        flexShrink: 0,
                        position: 'relative',
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        outline: '1px solid rgba(255,255,255,0.3)',
                        cursor: 'help',
                    }}
                >
                    {hasMarker && (
                        <>
                            <HorizontalStrip
                                histogram={histogram}
                                histogramLeftPct={histogramLeftPct}
                                histogramRightPct={histogramRightPct}
                                markerLeftPct={markerLeftPct}
                                markerRightPct={markerRightPct}
                                referenceMarkers={referenceMarkers}
                                barColor={colors.bar}
                                gradientEndColor={colors.gradientEnd}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    width: `${markerLeftPct}%`,
                                    bgcolor: WINDOW_SCRIM_COLOR,
                                    pointerEvents: 'none',
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: `${markerRightPct}%`,
                                    right: 0,
                                    bgcolor: WINDOW_SCRIM_COLOR,
                                    pointerEvents: 'none',
                                }}
                            />
                            <EdgeMarker leftPct={markerLeftPct} />
                            <EdgeMarker leftPct={markerRightPct} />
                        </>
                    )}
                </Box>
            </Tooltip>
            <Box
                sx={{
                    width: '100%',
                    height: LABELS_HEIGHT_PX,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexShrink: 0,
                }}
            >
                {[display.min, display.max].map((value, i) => (
                    <OverlayLabel
                        key={i}
                        title="Histogram clipped to the 2nd–99.5th percentile of voxel intensities; range extends past the data when the current window does"
                        sx={[
                            LABEL_PILL_SX,
                            {
                                fontSize: LABEL_FONT_SIZE_PX,
                                lineHeight: `${LABEL_FONT_SIZE_PX}px`,
                                cursor: 'help',
                            },
                        ]}
                    >
                        {formatValue(value)}
                    </OverlayLabel>
                ))}
            </Box>
        </Box>
    );
};

export default IntensityHistogramStrip;
